import styles from "./InfoEdit.module.css";
import PropTypes from "prop-types";
import { Button, Checkbubble, TextInput } from "@wwg/ui";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMerge } from "shared/hooks/useMerge";
import cx from "classnames";
import { TextArea } from "shared/components/TextArea/TextArea";
import { PREFERRED_DELIVERY } from "shared/enums";

/**
 * Form for editing customer info.
 */
export function InfoEdit({ info, onCancel, onSave, disabled }) {
  const { t } = useTranslation();
  const [_info, _mergeInfo] = useMerge(info);

  const _onSubmit = useCallback((e) => {
    e.preventDefault();
  }, []);

  const _onSave = useCallback(
    (e) => {
      e.preventDefault();

      onSave({
        ..._info,
      });
    },
    [_info, onSave]
  );

  const _onCancel = useCallback(
    (e) => {
      e.preventDefault();
      onCancel();
    },
    [onCancel]
  );

  return (
    <form className={styles.form} onSubmit={_onSubmit}>
      <section>
        <div className={styles.addressFields}>
          <div className={styles.inputs}>
            <TextInput
              value={_info.street}
              disabled={disabled}
              className={cx(styles.input)}
              placeholder={t("labels.addressStreetPlaceholder")}
              onChange={(e) => void _mergeInfo({ street: e.target.value })}
            />
            <div className={styles.row}>
              <TextInput
                value={_info.city}
                disabled={disabled}
                className={cx(styles.input, styles.medium)}
                placeholder={t("labels.addressCityPlaceholder")}
                onChange={(e) => void _mergeInfo({ city: e.target.value })}
              />

              <div className={styles.stateField}>
                <TextInput
                  value={_info.state}
                  disabled={disabled}
                  className={cx(styles.input, styles.stateFieldInput)}
                  placeholder={t("labels.addressStatePlaceholder")}
                  onChange={(e) => void _mergeInfo({ state: e.target.value })}
                />
              </div>
            </div>
            <div className={styles.row}>
              <TextInput
                value={_info.zip}
                disabled={disabled}
                className={cx(styles.input, styles.medium)}
                placeholder={t("labels.addressZipPlaceholder")}
                onChange={(e) => void _mergeInfo({ zip: e.target.value })}
              />
            </div>
          </div>

          <div className={styles.controls}>
            <Button
              className={styles.actionButton}
              disabled={disabled}
              buttonStyle="accent"
              onClick={_onSave}
            >
              {t("buttons.save")}
            </Button>
            <Button
              className={styles.actionButton}
              disabled={disabled}
              buttonStyle="hollowAccent"
              onClick={_onCancel}
            >
              {t("buttons.cancel")}
            </Button>
          </div>
        </div>
      </section>

      <div className={styles.split}>
        <section>
          <header>
            <h5>{t("client.clientNumber")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.clientNumber}
              disabled={disabled}
              className={cx(styles.input, styles.medium)}
              onChange={(e) =>
                void _mergeInfo({ clientNumber: e.target.value })
              }
            />
          </div>
        </section>
        <div />
      </div>

      <div className={styles.split}>
        <section>
          <header>
            <h5>{t("client.Occupation")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.occupation}
              className={styles.input}
              disabled={disabled}
              onChange={(e) => void _mergeInfo({ occupation: e.target.value })}
            />
          </div>
        </section>

        <section>
          <header>
            <h5>{t("client.Birthday")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.birthday}
              className={styles.input}
              disabled={disabled}
              onChange={(e) => void _mergeInfo({ birthday: e.target.value })}
            />
          </div>
        </section>
      </div>

      <div className={styles.split}>
        <section>
          <header>
            <h5>{t("client.Spouse")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.spouse}
              className={styles.input}
              disabled={disabled}
              onChange={(e) => void _mergeInfo({ spouse: e.target.value })}
            />
          </div>
        </section>

        <section>
          <header>
            <h5>{t("client.Anniversary")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.anniversary}
              className={styles.input}
              disabled={disabled}
              onChange={(e) => void _mergeInfo({ anniversary: e.target.value })}
            />
          </div>
        </section>
      </div>

      <hr />

      <div className={styles.split}>
        <section>
          <header>
            <h5>{t("client.dittoStartDate")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.dittoStartDate}
              className={styles.input}
              disabled={disabled}
              onChange={(e) =>
                void _mergeInfo({ dittoStartDate: e.target.value })
              }
            />
          </div>
        </section>

        <div />
      </div>

      <section>
        <header>
          <h5>{t("client.preferredDelivery")}</h5>
        </header>

        <div className={styles.fields}>
          <div className={styles.checkBubbleGroup}>
            <Checkbubble
              id="delivery-ground"
              disabled={disabled}
              isChecked={
                _info.preferredDeliveryMethod === PREFERRED_DELIVERY.Ground
              }
              onChange={() =>
                void _mergeInfo({
                  preferredDeliveryMethod: PREFERRED_DELIVERY.Ground,
                })
              }
            />

            <label htmlFor="delivery-ground">{t("client.Ground")}</label>
          </div>

          <div className={styles.checkBubbleGroup}>
            <Checkbubble
              id="delivery-expedited"
              disabled={disabled}
              isChecked={
                _info.preferredDeliveryMethod === PREFERRED_DELIVERY.Expedited
              }
              onChange={() =>
                void _mergeInfo({
                  preferredDeliveryMethod: PREFERRED_DELIVERY.Expedited,
                })
              }
            />

            <label htmlFor="delivery-expedited">{t("client.Expedited")}</label>
          </div>

          <div className={styles.checkBubbleGroup}>
            <Checkbubble
              id="delivery-amway"
              disabled={disabled}
              isChecked={
                _info.preferredDeliveryMethod ===
                PREFERRED_DELIVERY.AmwayPremium
              }
              onChange={() =>
                void _mergeInfo({
                  preferredDeliveryMethod: PREFERRED_DELIVERY.AmwayPremium,
                })
              }
            />

            <label htmlFor="delivery-amway">
              {t("client.premiumDelivery")}&trade;
            </label>
          </div>

          <div className={styles.checkBubbleGroup}>
            <Checkbubble
              id="delivery-hand"
              disabled={disabled}
              isChecked={
                _info.preferredDeliveryMethod ===
                PREFERRED_DELIVERY.HandDelivery
              }
              onChange={() =>
                void _mergeInfo({
                  preferredDeliveryMethod: PREFERRED_DELIVERY.HandDelivery,
                })
              }
            />

            <label htmlFor="delivery-hand">{t("client.handDelivery")}</label>
          </div>
        </div>
      </section>

      <div className={styles.split}>
        <section>
          <header>
            <h5>{t("client.gateCode")}</h5>
          </header>

          <div className={styles.fields}>
            <TextInput
              value={_info.gateCode}
              disabled={disabled}
              className={styles.input}
              onChange={(e) => void _mergeInfo({ gateCode: e.target.value })}
            />
          </div>
        </section>

        <div />
      </div>

      <section>
        <header>
          <h5>{t("client.specialLocationDropOff")}</h5>
        </header>

        <div className={styles.fields}>
          <TextInput
            value={_info.dropOffLocation}
            disabled={disabled}
            className={styles.input}
            onChange={(e) =>
              void _mergeInfo({ dropOffLocation: e.target.value })
            }
          />
        </div>
      </section>

      <hr />

      <section>
        <header>
          <h5>
            {t("client.dietaryPreferences")} <br />
            <span>{t("client.glutenFree+")}</span>
          </h5>
        </header>

        <div className={styles.fields}>
          <TextArea
            value={_info.dietaryPreferences}
            disabled={disabled}
            onChange={(e) =>
              void _mergeInfo({ dietaryPreferences: e.target.value })
            }
          />
        </div>
      </section>

      <section>
        <header>
          <h5>
            {t("client.Allergies")} <br />
            <span>{t("client.nuts+")}</span>
          </h5>
        </header>

        <div className={styles.fields}>
          <TextArea
            value={_info.allergies}
            disabled={disabled}
            onChange={(e) => void _mergeInfo({ allergies: e.target.value })}
          />
        </div>
      </section>

      <section>
        <header>
          <h5>{t("client.healthConcerns")}</h5>
        </header>

        <div className={styles.fields}>
          <TextArea
            value={_info.healthConcerns}
            disabled={disabled}
            onChange={(e) =>
              void _mergeInfo({ healthConcerns: e.target.value })
            }
          />
        </div>
      </section>
    </form>
  );
}

InfoEdit.propTypes = {
  info: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    clientNumber: PropTypes.string,
    occupation: PropTypes.string,
    birthday: PropTypes.string,
    spouse: PropTypes.string,
    anniversary: PropTypes.string,
    dittoStartDate: PropTypes.string,
    preferredDeliveryMethod: PropTypes.string,
    gateCode: PropTypes.string,
    dropOffLocation: PropTypes.string,
    dietaryPreferences: PropTypes.string,
    allergies: PropTypes.string,
    healthConcerns: PropTypes.string,
  }),

  /**
   * Called when the user clicks the Cancel button.
   */
  onCancel: PropTypes.func.isRequired,

  /**
   * Called when the user clicks the Save button. Receives an updated info object.
   */
  onSave: PropTypes.func.isRequired,
};
